/**
 * CompanySubModePage — owner picks operating mode (shipper / operator / both).
 *
 *   shipper  — يعمل كمرسل عادي مع branding الشركة
 *   operator — ينظّم ويشغّل (الوضع الكامل: محلية = trips, دولية = حاويات)
 *   both     — كلاهما
 *
 * Mirrors Flutter `company/company_sub_mode_page.dart`. Re-uses
 * `companiesApi.update` (the PUT endpoint accepts `operatorMode`).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Truck, Package, Layers, Loader, CheckCircle2 } from 'lucide-react';
import { useMyCompany, useUpdateOperatorMode } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import type { CompanyOperatorMode } from '@/types/company';

interface ModeOption {
  mode: CompanyOperatorMode;
  icon: typeof Truck;
  titleKey: string;
  fallbackTitle: string;
  descKey: string;
  fallbackDesc: string;
}

const OPTIONS: ModeOption[] = [
  {
    mode: 'shipper',
    icon: Package,
    titleKey: 'submode_shipper_title',
    fallbackTitle: 'مُرسل (Shipper)',
    descKey: 'submode_shipper_desc',
    fallbackDesc: 'أعمل كمرسل عادي. شحناتي تظهر باسم شركتي مع شعارها. لا أنظّم رحلات أو حاويات.',
  },
  {
    mode: 'operator',
    icon: Truck,
    titleKey: 'submode_operator_title',
    fallbackTitle: 'مُشغّل (Operator)',
    descKey: 'submode_operator_desc',
    fallbackDesc: 'أنظّم وأشغّل: شركة محلية = أسطول شاحنات + رحلات بحجم كبير. شركة دولية = حاويات مفتوحة + استقبال شحنات.',
  },
  {
    mode: 'both',
    icon: Layers,
    titleKey: 'submode_both_title',
    fallbackTitle: 'كلاهما (Both)',
    descKey: 'submode_both_desc',
    fallbackDesc: 'أعمل كمرسل + كمشغّل. مرونة كاملة لكن إعدادات أكثر للإدارة.',
  },
];

export default function CompanySubModePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading } = useMyCompany();
  const update = useUpdateOperatorMode(company?.id || 0);

  const [selected, setSelected] = useState<CompanyOperatorMode | null>(null);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  const current = company.operator_mode;
  const active = selected ?? current;

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1">
          {t('submode_title', 'وضع التشغيل')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('submode_subtitle', 'اختر كيف تعمل شركتك. يمكنك تغيير ذلك لاحقاً من الإعدادات.')}
        </p>

        <div className="space-y-3 mb-6">
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = active === opt.mode;
            return (
              <button
                key={opt.mode}
                type="button"
                onClick={() => setSelected(opt.mode)}
                className={`w-full text-right p-4 rounded-2xl border-2 transition-all ${
                  isActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-surface hover:border-primary/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl ${isActive ? 'bg-primary text-white' : 'bg-border/40 text-text-secondary'}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-text-primary">
                        {t(opt.titleKey, opt.fallbackTitle)}
                      </h3>
                      {current === opt.mode && (
                        <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded-full">
                          {t('submode_current', 'الحالي')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary leading-relaxed">
                      {t(opt.descKey, opt.fallbackDesc)}
                    </p>
                  </div>
                  {isActive && <CheckCircle2 size={20} className="text-primary flex-shrink-0 mt-1" />}
                </div>
              </button>
            );
          })}
        </div>

        <Button
          variant="primary"
          fullWidth
          loading={update.isPending}
          disabled={!selected || selected === current}
          onClick={async () => {
            if (!selected || selected === current) return;
            await update.mutateAsync(selected);
            navigate('/company', { replace: true });
          }}
        >
          {t('save_changes', 'حفظ التعديلات')}
        </Button>
      </div>
    </AppLayout>
  );
}
